import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, MapPin, Code, Globe, Rocket } from 'lucide-react';

const AboutPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>About Us - iModernize LLC | Web Design & Development in New Hampshire</title>
        <meta name="description" content="iModernize LLC is a New Hampshire-based web design and development agency. We specialize in SEO, web redesigns, hosting, and custom app development." />
        <meta name="keywords" content="about iModernize, web design agency New Hampshire, Merrimack NH web developer, iModernize LLC" />
      </Helmet>

      <div className="py-20">
        <div className="container-custom">
          {/* Header Section */}
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About iModernize
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Building Digital Excellence for Modern Businesses
            </p>
          </div>

          {/* Mission Section */}
          <div className="max-w-4xl mx-auto mb-16">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-4">
                We are a full-service web development agency based in Merrimack, New Hampshire, 
                specializing in creating beautiful, functional websites and applications that drive real results 
                for businesses of all sizes.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                Our mission is to help businesses succeed online by delivering exceptional digital solutions — 
                from SEO optimization and complete website redesigns to custom application development and reliable hosting.
              </p>
            </div>
          </div>

          {/* What We Do */}
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What We Do</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
                <div className="flex items-center mb-3">
                  <div className="p-2 rounded-lg bg-blue-100 mr-3">
                    <Globe className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">SEO Optimization</h3>
                </div>
                <p className="text-gray-600">Comprehensive SEO audits and fixups to boost your search engine rankings and drive organic traffic.</p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
                <div className="flex items-center mb-3">
                  <div className="p-2 rounded-lg bg-blue-100 mr-3">
                    <Rocket className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Web Redesign</h3>
                </div>
                <p className="text-gray-600">Modern, responsive website redesigns that convert visitors into customers with clean, fast-loading designs.</p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
                <div className="flex items-center mb-3">
                  <div className="p-2 rounded-lg bg-blue-100 mr-3">
                    <Code className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Custom App Development</h3>
                </div>
                <p className="text-gray-600">Bespoke web applications built with modern frameworks like React, Node.js, and Python to solve your unique business challenges.</p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
                <div className="flex items-center mb-3">
                  <div className="p-2 rounded-lg bg-blue-100 mr-3">
                    <MapPin className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Hosting & Management</h3>
                </div>
                <p className="text-gray-600">Reliable, managed hosting with SSL, daily backups, security monitoring, and ongoing maintenance.</p>
              </div>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-blue-600 rounded-2xl p-10 text-white">
              <h2 className="text-3xl font-bold mb-4">Ready to Work Together?</h2>
              <p className="text-blue-100 text-lg mb-6">
                Get in touch to discuss your project and see how we can help your business grow online.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href="mailto:nikos.local.web.design@gmail.com"
                  className="inline-flex items-center px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  nikos.local.web.design@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutPage;